import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Upload, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

export default function TestimonialManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name:'',title:'',company:'',content:'',rating:5,position:0,is_active:true });
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // References settings: version + partner logos
  const [version, setVersion] = useState('v1');
  const [logos, setLogos] = useState([]);
  const [savingRef, setSavingRef] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const fetch = useCallback(() => { api.get('/testimonials').then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => {
    fetch();
    api.get('/settings/references')
      .then(r => {
        setVersion(r.data?.version === 'v2' ? 'v2' : 'v1');
        setLogos(Array.isArray(r.data?.logos) ? r.data.logos : []);
      })
      .catch(() => {});
  }, [fetch]);

  const saveReferences = async (nextVersion, nextLogos) => {
    const v = nextVersion ?? version;
    const l = nextLogos ?? logos;
    setSavingRef(true);
    try {
      await api.put('/settings/references', { version: v, logos: l });
      toast.success('Referans ayarları kaydedildi');
    } catch (err) {
      toast.error('Ayar kaydedilemedi');
    } finally {
      setSavingRef(false);
    }
  };

  const handleVersionChange = (v) => {
    setVersion(v);
    saveReferences(v, logos);
  };

  const handleLogoUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingLogo(true);
    try {
      const added = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const r = await api.post('/uploads', fd);
        added.push({ image_path: r.data.path, name: '', link: '' });
      }
      const next = [...logos, ...added];
      setLogos(next);
      await saveReferences(version, next);
    } catch (err) {
      toast.error('Logo yüklenemedi');
    } finally {
      setUploadingLogo(false);
    }
  };

  const updateLogo = (idx, key, value) => {
    setLogos(ls => ls.map((l, i) => i === idx ? { ...l, [key]: value } : l));
  };

  const removeLogo = async (idx) => {
    const removed = logos[idx];
    const next = logos.filter((_, i) => i !== idx);
    setLogos(next);
    await saveReferences(version, next);
    if (removed?.image_path) {
      try { await api.post('/uploads/delete', { paths: [removed.image_path] }); } catch (_) { /* non-blocking */ }
    }
  };

  const resetForm = () => { setForm({ name:'',title:'',company:'',content:'',rating:5,position:0,is_active:true }); setImageFile(null); setEditing(null); setShowForm(false); };
  const handleEdit = (t) => { setEditing(t); setForm({ name:t.name||'',title:t.title||'',company:t.company||'',content:t.content||'',rating:parseInt(t.rating)||5,position:parseInt(t.position)||0,is_active:t.is_active===true||t.is_active==='true'||t.is_active==='True' }); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.content.trim()) { toast.error('Ad ve yorum gerekli'); return; }
    setIsSubmitting(true);
    try {
      let img = editing?.image_path || '';
      if (imageFile) { const fd = new FormData(); fd.append('file', imageFile); const r = await api.post('/uploads', fd); img = r.data.path; }
      const payload = { ...form, image_path: img };
      if (editing) { await api.put(`/testimonials/${editing.id}`, payload); toast.success('Güncellendi'); }
      else { await api.post('/testimonials', payload); toast.success('Eklendi'); }
      resetForm(); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata oluştu'); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Silmek istediğinize emin misiniz?')) return; try { await api.delete(`/testimonials/${id}`); toast.success('Silindi'); fetch(); } catch { toast.error('Hata'); } };

  return (
    <div data-testid="admin-testimonial-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">Referanslar</h1>
        {version === 'v1' && (
          <button onClick={() => { resetForm(); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium"><Plus className="w-4 h-4" /> Yeni Referans</button>
        )}
      </div>

      {/* Version selector */}
      <div className="mb-6 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]" data-testid="reference-version-selector">
        <h3 className="font-heading text-lg font-semibold mb-1">Sayfa Görünümü</h3>
        <p className="text-xs text-muted-foreground mb-4">Referanslar sayfasının nasıl görüneceğini seçin.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleVersionChange('v1')}
            disabled={savingRef}
            className={`text-left p-4 rounded-xl border transition-colors ${version === 'v1' ? 'border-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.08)]' : 'border-border hover:bg-secondary'}`}
            data-testid="reference-version-v1"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-3.5 h-3.5 rounded-full border-2 ${version === 'v1' ? 'border-[hsl(var(--gold))] bg-[hsl(var(--gold))]' : 'border-muted-foreground'}`} />
              <span className="font-medium text-sm">Versiyon 1 — Müşteri Yorumları</span>
            </div>
            <p className="text-xs text-muted-foreground ml-5.5">Yorum, puan ve kişi bilgisi içeren kartlar.</p>
          </button>
          <button
            type="button"
            onClick={() => handleVersionChange('v2')}
            disabled={savingRef}
            className={`text-left p-4 rounded-xl border transition-colors ${version === 'v2' ? 'border-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.08)]' : 'border-border hover:bg-secondary'}`}
            data-testid="reference-version-v2"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-3.5 h-3.5 rounded-full border-2 ${version === 'v2' ? 'border-[hsl(var(--gold))] bg-[hsl(var(--gold))]' : 'border-muted-foreground'}`} />
              <span className="font-medium text-sm">Versiyon 2 — Firma Logoları</span>
            </div>
            <p className="text-xs text-muted-foreground ml-5.5">Birlikte çalışılan firmaların logolarını listeler.</p>
          </button>
        </div>
      </div>

      {/* V2: Logo manager */}
      {version === 'v2' && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]" data-testid="reference-logo-manager">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Firma Logoları</h3>
              <p className="text-xs text-muted-foreground mt-1">Logolar sabit yükseklikte, oranı korunarak (bozulmadan) gösterilir.</p>
            </div>
            <label className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] ${uploadingLogo ? 'opacity-60 pointer-events-none' : ''}`} data-testid="reference-logo-add">
              <Upload className="w-4 h-4" /> {uploadingLogo ? 'Yükleniyor...' : 'Logo Ekle'}
              <input type="file" accept="image/*" multiple className="hidden" onChange={e => { handleLogoUpload(Array.from(e.target.files)); e.target.value = ''; }} />
            </label>
          </div>

          {logos.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">Henüz logo eklenmedi. "Logo Ekle" ile başlayın.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {logos.map((logo, i) => (
                <div key={i} className="p-4 rounded-xl border border-border bg-secondary/30" data-testid={`reference-logo-item-${i}`}>
                  <div className="relative">
                    <div className="h-24 w-full rounded-lg bg-white border border-border flex items-center justify-center p-4 mb-3">
                      <img src={getFileUrl(logo.image_path)} alt="" className="max-h-full max-w-full object-contain" />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeLogo(i)}
                      className="absolute -top-2 -right-2 p-1 rounded-full bg-destructive text-white shadow-md hover:opacity-90"
                      title="Logoyu kaldır"
                      data-testid={`reference-logo-remove-${i}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <input
                    value={logo.name || ''}
                    onChange={e => updateLogo(i, 'name', e.target.value)}
                    onBlur={() => saveReferences(version, logos)}
                    placeholder="Firma adı (opsiyonel)"
                    className="w-full mb-2 px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                  />
                  <input
                    value={logo.link || ''}
                    onChange={e => updateLogo(i, 'link', e.target.value)}
                    onBlur={() => saveReferences(version, logos)}
                    placeholder="Web sitesi (opsiyonel)"
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* V1: Testimonials CRUD */}
      {version === 'v1' && (
        <>
          {showForm && (
            <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
              <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Düzenle' : 'Yeni Referans'}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Inp label="Ad Soyad *" value={form.name} onChange={v => setForm(f=>({...f,name:v}))} />
                  <Inp label="Ünvan" value={form.title} onChange={v => setForm(f=>({...f,title:v}))} placeholder="Genel Müdür" />
                  <Inp label="Şirket" value={form.company} onChange={v => setForm(f=>({...f,company:v}))} />
                </div>
                <div><label className="block text-sm font-medium mb-1.5">Yorum *</label><textarea value={form.content} onChange={e => setForm(f=>({...f,content:e.target.value}))} rows={3} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm resize-none" /></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><label className="block text-sm font-medium mb-1.5">Puan (1-5)</label><input type="number" min={1} max={5} value={form.rating} onChange={e => setForm(f=>({...f,rating:parseInt(e.target.value)||5}))} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm" /></div>
                  <Inp label="Sıra" type="number" value={form.position} onChange={v => setForm(f=>({...f,position:parseInt(v)||0}))} />
                  <div><label className="block text-sm font-medium mb-1.5">Fotoğraf</label><label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary"><Upload className="w-4 h-4" /> Seç<input type="file" accept="image/*" className="hidden" onChange={e => setImageFile(e.target.files[0])} /></label></div>
                </div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_active} onChange={e => setForm(f=>({...f,is_active:e.target.checked}))} className="rounded" /> Aktif</label>
                <div className="flex gap-3">
                  <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50">{isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}</button>
                  <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
                </div>
              </form>
            </div>
          )}

          {loading ? <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
          : items.length === 0 ? <div className="text-center py-10 text-muted-foreground">Henüz referans yok</div>
          : <div className="space-y-3">{items.map(t => (
            <div key={t.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary shrink-0">{t.image_path ? <img src={getFileUrl(t.image_path)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs font-bold text-muted-foreground">{t.name?.[0]}</div>}</div>
              <div className="flex-1 min-w-0"><div className="font-medium text-sm">{t.name}</div><div className="text-xs text-muted-foreground">{t.company} {t.title && `- ${t.title}`}</div></div>
              <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} className={`w-3 h-3 ${i <= (parseInt(t.rating)||5) ? 'text-[hsl(var(--gold))] fill-[hsl(var(--gold))]' : 'text-gray-300'}`} />)}</div>
              <div className="flex gap-1">
                <button onClick={() => handleEdit(t)} className="p-2 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(t.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}</div>}
        </>
      )}
    </div>
  );
}
const Inp = ({ label, value, onChange, type='text', placeholder }) => (<div><label className="block text-sm font-medium mb-1.5">{label}</label><input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" /></div>);
